let calculator;
function vueCalc() {
    calculator = Vue.createApp({
        delimiters: ['#{', '}'],
        setup() {
            const globalTools = window.globalTools();

            const sameRim = Vue.ref(false);
            const downSteps = Vue.ref(false);
            const upSteps = Vue.ref(false);
            const widthInput = Vue.ref('');
            const aspectInput = Vue.ref('');
            const rimInput = Vue.ref('');
            const tolerance = Vue.ref(3.5);
            const widthCompA = Vue.ref('');
            const aspectCompA = Vue.ref('');
            const rimCompA = Vue.ref('');
            const widthCompB = Vue.ref('');
            const aspectCompB = Vue.ref('');
            const rimCompB = Vue.ref('');
            const altPane = Vue.ref(true);
            const widths = Vue.ref(false);
            const rims = Vue.ref(false);
            const aspects = Vue.ref(false);
            const validityTree = Vue.ref(false);
            const outcomeA = Vue.ref(false);
            const outcomeB = Vue.ref(false);
            const diff = Vue.ref(false);
            const diffPct = Vue.ref(false);
            const indicatedAt100 = Vue.ref(false);
            const varName = Vue.ref(btoa('calcData'));
            const useTracker = Vue.ref(false);
            const now = Vue.ref(Date.parse(new Date()));
            const execute = Vue.ref(true);
            const timeLimit = Vue.ref(false);
            const useLimit = Vue.ref(false);

            const showResults = Vue.computed(() => sameRim.value || downSteps.value || upSteps.value ? true : false);
            const allowAltSearch = Vue.computed(() => widthInput.value != '' && aspectInput.value != '' && rimInput.value != '' && tolerance.value != '' && execute.value ? false : true);
            const allowComp = Vue.computed(() => widthCompA.value != '' && aspectCompA.value != '' && rimCompA.value != '' && widthCompB.value != '' && aspectCompB.value != '' && rimCompB.value != '' && execute.value ? false : true);
            const showCompResults = Vue.computed(() => outcomeA.value && outcomeB.value && diff.value && diffPct.value && indicatedAt100.value ? true : false);

            const MM_PER_INCH = 25.4;
            const MM_PER_KM = 1_000_000;

            const pureRounding = (num) => {
                return Math.round((num + Number.EPSILON) * 100) / 100;
            }

            const tyreMetrics = (width, aspect, rim) => {
                const sidewall = pureRounding(width * (aspect / 100));
                const diameter = pureRounding((rim * MM_PER_INCH) + 2 * sidewall);
                const circumference = pureRounding(diameter * Math.PI);
                return {
                    width, aspect, rim,
                    sidewall, diameter, circumference,
                    revsPerKm: MM_PER_KM / circumference,
                };
            }

            const compare = () => {
                if(useTracker?.value && now?.value && timeLimit?.value && useLimit?.value) {
                    useTracker.value.uses++
                    if (useTracker?.value?.originTime && (now?.value - useTracker?.value?.originTime) > timeLimit?.value) {
                        useTracker.value = {
                            uses: 0,
                            originTime: now.value
                        };
                    } else if (useTracker?.value?.uses > useLimit.value) {
                        execute.value = false;
                    }
                    localStorage.setItem(varName.value, btoa(JSON.stringify(useTracker.value)));
                }
                if (!execute.value) return;
                outcomeA.value = tyreMetrics(widthCompA.value, aspectCompA.value, rimCompA.value);
                outcomeB.value = tyreMetrics(widthCompB.value, aspectCompB.value, rimCompB.value);
                diff.value = {
                    sidewall: (outcomeB.value.sidewall - outcomeA.value.sidewall).toFixed(1),
                    diameter: (outcomeB.value.diameter - outcomeA.value.diameter).toFixed(1),
                    circumference: (outcomeB.value.circumference - outcomeA.value.circumference).toFixed(1),
                    revsPerKm: (outcomeB.value.revsPerKm - outcomeA.value.revsPerKm).toFixed(1)
                }
                diffPct.value = {
                    sidewall: (((outcomeB.value.sidewall - outcomeA.value.sidewall) / outcomeA.value.sidewall) * 100).toFixed(2),
                    diameter: (((outcomeB.value.diameter - outcomeA.value.diameter) / outcomeA.value.diameter) * 100).toFixed(2),
                    circumference: (((outcomeB.value.circumference - outcomeA.value.circumference) / outcomeA.value.circumference) * 100).toFixed(2),
                    revsPerKm: (((outcomeB.value.revsPerKm - outcomeA.value.revsPerKm) / outcomeA.value.revsPerKm) * 100).toFixed(2)
                }
                indicatedAt100.value = (100 * (outcomeB.value.circumference / outcomeA.value.circumference)).toFixed(2);
            }

            const findAlternatives = (width, aspect, rim, tolerancePct = 3.5) => {
                const target = tyreMetrics(width, aspect, rim);
                const matches = [];
                for (const w of widths?.value)
                    for (const a of aspects?.value)
                        for (const r of rims?.value) {
                            if (w === width && a === aspect && r === rim) continue;
                            const m = tyreMetrics(w, a, r);
                            const diffPct = ((m.circumference - target.circumference) / target.circumference) * 100;
                            if (Math.abs(diffPct) <= tolerancePct) matches.push({ ...m, diffPct });
                        }
                return matches.sort((x, y) => Math.abs(x.diffPct) - Math.abs(y.diffPct));
            }

            const verifyAlternatives = (alts) => {
                if (!validityTree.value) return false;
                let keys = Object.keys(validityTree?.value);
                let newData = []
                for (let i = 0; i < keys.length; i++) {
                    newData = newData.concat(validityTree?.value[keys[i]].flatMap((item) => {
                        let idx = alts.findIndex(a => a.width === item.width && a.aspect === item.aspect && a.rim === Number(keys[i]))
                        if (idx > -1) {
                            return {
                                circumference: alts[idx].circumference,
                                diameter: alts[idx].diameter,
                                diffPct: alts[idx].diffPct,
                                revsPerKm: alts[idx].revsPerKm,
                                rim: alts[idx].rim,
                                sidewall: alts[idx].sidewall,
                                aspect: item.aspect,
                                width: item.width,
                                tyreUrl: toRootPath(item.tyreUrl)
                            }
                        } else {
                            return []
                        }
                    }))
                }
                let result = Array.from(new Set(newData.map(obj => JSON.stringify(obj)))).map(str => JSON.parse(str));

                return result;
            }

            const sortAlternatives = (alts, rim) => {
                let equal = alts.filter(alt => alt.rim === rim);
                let sortedEqual = equal.sort((a, b) => Math.abs(a.diffPct) - Math.abs(b.diffPct));
                let up = alts.filter(alt => alt.rim > rim);
                let sortedup = up.sort((a, b) => Math.abs(a.diffPct) - Math.abs(b.diffPct));
                let down = alts.filter(alt => alt.rim < rim);
                let sortedDown = down.sort((a, b) => Math.abs(a.diffPct) - Math.abs(b.diffPct));
                return {
                    equal: sortedEqual,
                    upSteps: sortedup,
                    downSteps: sortedDown
                };
            }

            const processAlternatives = () => {
                if(useTracker?.value && now?.value && timeLimit?.value && useLimit?.value) {
                    useTracker.value.uses++

                    if (useTracker?.value?.originTime && (now?.value - useTracker?.value?.originTime) > timeLimit?.value) {
                        useTracker.value = {
                            uses: 0,
                            originTime: now.value
                        };
                    } else if (useTracker?.value?.uses > useLimit.value) {
                        execute.value = false;
                    }
                    localStorage.setItem(varName.value, btoa(JSON.stringify(useTracker.value)));
                }
                if (!execute.value) return;
                const alts = findAlternatives(widthInput.value, aspectInput.value, rimInput.value, tolerance.value);
                const validAlts = verifyAlternatives(alts);
                const sortedAlts = sortAlternatives(validAlts, rimInput.value);
                sameRim.value = sortedAlts?.equal?.length > 0 ? sortedAlts.equal : false;
                downSteps.value = sortedAlts?.downSteps?.length > 0 ? sortedAlts.downSteps : false;
                upSteps.value = sortedAlts?.upSteps?.length > 0 ? sortedAlts.upSteps : false;
            }

            const togglePane = (val) => {
                if (val) {
                    altPane.value = !altPane.value;
                }
            }

            const parseValue = (val) => {
                try {
                    return JSON.parse(val);
                } catch (e) {
                    console.error(`Error parsing metaobject response: ${e}`)
                    return false;
                }
            }

            const toRootPath = (path) => {
                const clean = path.replace(/^\.\//, '').replace(/^\/+/, '');
                return `/${clean}`;
            }

            const isEven = (num) => {
                if (num % 2 === 0) {
                    return true;
                } else {
                    return false;
                }
            }

            Vue.onMounted(async () => {
                if (limits) {
                    if (limits?.time) {
                        timeLimit.value = 1000 * 60 * 60 * 24 * limits?.time || 0;
                    }
                    if (limits?.uses) {
                        useLimit.value = limits?.uses || 30;
                    }
                    if (localStorage.getItem(varName.value)) {
                        let calcObj = localStorage.getItem(varName.value);
                        try {
                            let decodeObj = atob(calcObj);
                            let parsed = JSON.parse(decodeObj);
                            useTracker.value = parsed;
                            if (parsed?.originTime && (now.value - parsed.originTime) > timeLimit.value) {
                                useTracker.value = {
                                    uses: 0,
                                    originTime: now.value
                                };
                                localStorage.setItem(varName.value, btoa(JSON.stringify(useTracker.value)));
                            } else if (parsed?.uses > useLimit.value) {
                                execute.value = false;
                            }
                        } catch (e) {
                            execute.value = true;
                            if (!useTracker.value) {
                                useTracker.value = {
                                    uses: 0,
                                    originTime: now.value
                                };
                            }
                            localStorage.setItem(varName.value, btoa(JSON.stringify(useTracker.value)));
                        }
                    } else {
                        execute.value = true;
                        if (!useTracker.value) {
                            useTracker.value = {
                                uses: 0,
                                originTime: now.value
                            };
                        }
                        localStorage.setItem(varName.value, btoa(JSON.stringify(useTracker.value)));
                    }
                } else {
                    execute.value = true;
                }
                if (metaSource) {
                    let queryString = `{
	                metaobject(id:"gid://shopify/Metaobject/${metaSource}") {
		                fields {				
			                key
			                value
		                }		
	                }
                }`
                    let metaResponse = await globalTools.genericQuery(queryString);
                    if (metaResponse?.data?.metaobject?.fields?.length > 0) {
                        for (let i = 0; i < metaResponse?.data?.metaobject?.fields?.length; i++) {
                            if (metaResponse.data.metaobject.fields[i].key === 'width') {
                                widths.value = parseValue(metaResponse.data.metaobject.fields[i].value);
                            } else if (metaResponse.data.metaobject.fields[i].key === 'aspect') {
                                aspects.value = parseValue(metaResponse.data.metaobject.fields[i].value);
                            } else if (metaResponse.data.metaobject.fields[i].key === 'rim') {
                                rims.value = parseValue(metaResponse.data.metaobject.fields[i].value);
                            } else if (metaResponse.data.metaobject.fields[i].key === 'legitimate') {
                                validityTree.value = parseValue(metaResponse.data.metaobject.fields[i].value);
                            }
                        }
                    }
                }
            })

            return {
                widths,
                aspects,
                rims,
                sameRim,
                downSteps,
                upSteps,
                showResults,
                widthInput,
                aspectInput,
                rimInput,
                allowAltSearch,
                allowComp,
                widthCompA,
                aspectCompA,
                rimCompA,
                widthCompB,
                aspectCompB,
                rimCompB,
                altPane,
                tolerance,
                outcomeA,
                outcomeB,
                diff,
                diffPct,
                indicatedAt100,
                showCompResults,
                processAlternatives,
                compare,
                togglePane,
                isEven
            };
        },
    })

    if (window.VueAppGlobals?.pinia) calculator.use(window.VueAppGlobals.pinia);
    calculator.mount('#calculator');
}


if (window.Vue && window.Pinia) {
    vueCalc();
} else {
    window.addEventListener('vue-ready', vueCalc);
}